import React from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import EmptyData from "go-core/components/EmptyData";
import FormatDate, { formatFormDateToString } from "go-core/components/Formatters/FormatDate";
import { ReactComponent as AlertSVG } from "go-core/images/svg/alert.svg";
import { ReactComponent as FileSVG } from "go-core/images/svg/file.svg";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import {
	SubscriptionInvoiceApi,
	SubscriptionVisibility,
} from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";

interface Props {
	invoices: SubscriptionInvoiceApi[];
	subscriptionsVisibility: SubscriptionVisibility;
	unPaidProformas: SubscriptionInvoiceApi[];
}

const SubscriptionsInvoice = ({ invoices, unPaidProformas }: Props) => {
	const organization = useSelector(selectOrganization);
	const date = new Date();
	const { t } = useTranslation();

	const renderTitleWhenUnpaidProformas = () => {
		const unpaidProformaWithEarliestDueDate = unPaidProformas.sort(
			(a, b) => Number(formatFormDateToString(a.expiry_date)) - Number(formatFormDateToString(b.expiry_date))
		)[0];
		const dueDate = FormatDate(unpaidProformaWithEarliestDueDate.expiry_date);
		const proformaDueMonth = Number(dueDate.split(".")[1]);
		const proformaDueDay = Number(dueDate.split(".")[0]);
		const proformaDueYear = Number(dueDate.split(".")[2]);
		const today = new Date();
		const day = today.getDate();
		const month = today.getMonth() + 1;
		const year = today.getFullYear();

		if (proformaDueMonth === month && day === proformaDueDay && year === proformaDueYear) {
			return t("modules.subscription.field.today_is_a_due_date.title");
		}
		if (formatFormDateToString(dueDate).getTime() < today.getTime()) {
			return `${t("modules.subscription.field.expired_due_date.title")} ${dueDate}`;
		}
		return `${t("modules.subscription.field.settling_payment.title")} ${dueDate}`;
	};

	return (
		<Card className={"subscription-info-card"}>
			<Card.Body>
				<div className="d-flex align-items-center mb-3">
					<div className="subscription-card-icon-box me-2">
						<FileSVG />
					</div>
					<h5 className="mb-0">{t("modules.invoice.header.title")}</h5>
				</div>
				{unPaidProformas.length > 0 && (
					<div className={"d-flex flex-column"}>
						<div className={"text-danger"}>
							<div className={"d-flex"}>
								<AlertSVG className={"mt-1 me-1"} />
								<div>
									<div className={"text-danger"}>
										<span>
											{t("modules.subscription.field.unpaid_proformas.title")}
											{unPaidProformas.length}
										</span>
									</div>
									<div className={"text-danger"}>{renderTitleWhenUnpaidProformas()}</div>
								</div>
							</div>
						</div>
					</div>
				)}
				{!unPaidProformas.length && (
					<>
						{invoices.length === 0 ? (
							<EmptyData title={""} description="W tym miesiącu nie masz żadnych faktur" />
						) : (
							<div className={"d-flex"}>
								<span>
									{t("modules.subscription.field.since_last_month.title")}
									<Link
										className={"mx-1"}
										to={`/${organization.id}/subscriptions/invoices?f=${btoa(
											unescape(
												encodeURIComponent(
													`${NEW_WAY_TO_ENCODING_FILTER_SIGN}due_date|gt=${date.getFullYear()}-${
														date.getMonth() < 10 ? `0${date.getMonth()}` : date.getMonth()
													}-01`
												)
											)
										)}`}
									>
										{t("modules.subscription.fields.invoices.title", {
											count: invoices.length,
										})}
									</Link>
									{t("modules.subscription.field.for_amount.title")}
									{invoices
										.reduce((acc, curr) => acc + (curr?.total_money?.amount || 0), 0)
										.toFixed(2)}{" "}
									PLN
								</span>
							</div>
						)}
					</>
				)}
				<div className={"card-body-footer"}>
					<Link rel="noreferrer" to={`/${organization.id}/subscriptions/invoices`}>
						{t("modules.subscription.action.show_all_invoices.title")}
					</Link>
				</div>
			</Card.Body>
		</Card>
	);
};
export default SubscriptionsInvoice;
